import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Globe } from 'lucide-react';
import { useLanguage, languages } from '@/context/LanguageContext';
import { routeConfig, supportedLanguages, getRouteKeyFromPath } from '@/config/routes';

export const LanguageSwitcher = ({ variant = 'default' }) => {
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const currentLang = languages[language];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (newLang) => {
    if (newLang === language) {
      setIsOpen(false);
      return;
    }

    // Get current path parts
    const pathParts = location.pathname.split('/').filter(Boolean);
    
    // Check if first part is a language code
    const hasLangPrefix = supportedLanguages.includes(pathParts[0]);
    
    if (hasLangPrefix) {
      // Get the route segment (e.g., 'diensten' from '/nl/diensten')
      const routeSegment = pathParts[1] || '';
      
      // Find the route key for this segment
      const routeKey = getRouteKeyFromPath(routeSegment);
      
      if (routeKey && routeConfig[routeKey]) {
        // Get the localized path for the new language
        const newPath = routeConfig[routeKey][newLang] || routeConfig[routeKey]['nl'];
        
        // Handle sub-paths (e.g., /nl/blog/article-slug)
        const subPath = pathParts.slice(2).join('/');
        const fullPath = newPath === '/' 
          ? `/${newLang}${subPath ? '/' + subPath : ''}`
          : `/${newLang}/${newPath}${subPath ? '/' + subPath : ''}`;
        
        navigate(fullPath);
      } else {
        // Unknown route, just swap the language prefix
        pathParts[0] = newLang;
        navigate('/' + pathParts.join('/'));
      }
    } else {
      // No language prefix, add one
      navigate(`/${newLang}${location.pathname}`);
    }

    setLanguage(newLang, true); // Mark as user choice
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 rounded-full transition-all ${
          variant === 'footer'
            ? 'bg-white/10 text-white hover:bg-white/20'
            : 'bg-slate-100 text-[#0f172a] hover:bg-slate-200'
        }`}
        data-testid="language-switcher"
        aria-label="Select language"
      >
        <Globe size={16} />
        <span className="text-sm font-medium">{currentLang.code.toUpperCase()}</span>
        <ChevronDown 
          size={14} 
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className={`absolute right-0 mt-2 py-2 rounded-xl shadow-xl border z-50 min-w-[160px] ${
              variant === 'footer'
                ? 'bg-[#1a1f2e] border-white/10'
                : 'bg-white border-slate-200'
            }`}
            data-testid="language-dropdown"
          >
            {Object.values(languages).map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleSelect(lang.code)}
                className={`w-full px-4 py-2 text-left flex items-center gap-3 transition-colors ${
                  variant === 'footer'
                    ? `text-white/80 hover:bg-white/10 ${language === lang.code ? 'bg-white/10 text-white' : ''}`
                    : `text-[#475569] hover:bg-slate-50 ${language === lang.code ? 'bg-slate-100 text-[#2563EB]' : ''}`
                }`}
                data-testid={`lang-option-${lang.code}`}
              >
                <span className="text-lg">{lang.flag}</span>
                <span className="text-sm font-medium">{lang.name}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
